import React, { useRef } from 'react';
import Card from 'react-bootstrap/Card';
import html2canvas from 'html2canvas';
import WDMatchlessLogo from '../../assets/WDMatchlessLogo.png';
import YearBadge from './YearBadge';
import WDSerialBadge from './WDSerialBadge';
import placeholderImage from '../../assets/bike_silhoutte.png';
import './WDRecordCard.css';
import { BsDownload } from "react-icons/bs";

const WDRecordCard = ({ record }) => {
  const cardRef = useRef(null);

  // --- Tank image (safe require) ---
  let tankImage = null;
  try {
    if (record.Tank) {
      tankImage = require(`../../assets/tanks/${record.Tank}`);
    }
  } catch {
    tankImage = null;
  }

  // --- Bike image based on model ---
  let bikeImage = placeholderImage;

  switch (record.Model) {
    case 'Silver Arrow':
      bikeImage = require('../../assets/bikes/MatchlessSilverArrow_transparent.png');
      break;
    case 'G3WO':
      bikeImage = require('../../assets/bikes/MatchlessG3WO_transparent.png');
      break;
    case 'G3L':
      bikeImage = require('../../assets/bikes/MatchlessWDG3L_transparent.png');
      break;
    default:
      bikeImage = placeholderImage;
      break;
  }

  const militaryClass = record['Military Class'] || '';
  const modelCode = (record.Model || '').toUpperCase();

  const handleDownloadImage = async () => {
    if (!cardRef.current) return;

    const canvas = await html2canvas(cardRef.current, {
      scale: 2,              // higher resolution
      useCORS: true,
      backgroundColor: null, // keep transparent around the poster
    });

    const dataUrl = canvas.toDataURL('image/png');

    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = `${modelCode || 'WD-Matchless'}.png`;
    link.click();
  };

  return (
    <Card className="wd-record-card">
      {/* Everything inside here becomes the image */}
      <div ref={cardRef} style={{ position: 'relative' }}>
        <div className="wd-scale-wrapper">
          {/* Outer khaki shell */}
          <div
            className="wd-outer-shell"
            style={{
              background: '#d8cbb0',
              borderRadius: '18px',
              padding: '1px',
              boxShadow: '0 18px 40px rgba(0,0,0,0.22)',
            }}
          >
            {/* Inner “paper” panel */}
            <div
              className="wd-inner-paper"
              style={{
                background: '#f7f1e3',
                borderRadius: '14px',
                border: '1px solid #d2c9b2',
                overflow: 'hidden',
              }}
            >
              {/* TOP ROW: STENCIL + BADGES */}
              <div
                className="wd-card-toprow"
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  padding: '50px 30px 0px',
                  gap: '160px',
                  width: '100vw',
                  textAlign: 'left',
                }}
              >
                {/* LEFT: big stencilled WD + model */}
                <div>
                  <div
                    className="wd-stencil-wd"
                    style={{
                      fontFamily:
                        '"ARMYRUST", "Impact", "Arial Black", system-ui, sans-serif',
                      fontSize: '140px',
                      letterSpacing: '0.05em',
                      color: '#4D4B1D',
                      textTransform: 'uppercase',
                      lineHeight: 0.3,
                    }}
                  >
                    WD
                  </div>
                  <div
                    className="wd-stencil-model"
                    style={{
                      fontFamily:
                        '"ARMYRUST", "Impact", "Arial Black", system-ui, sans-serif',
                      fontSize: '80px',
                      letterSpacing: '0.08em',
                      color: '#4D4B1D',
                      textTransform: 'uppercase',
                      marginTop: '1px',
                    }}
                  >
                    {modelCode}
                  </div>
                </div>

                {/* RIGHT: badge stack */}
                <div
                  className="wd-badge-stack"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    gap: '10px',
                    marginTop: '15px',
                    width: '240px',
                  }}
                >
                  <div
                    style={{
                      marginTop: '15px',
                      alignItems: 'flex-start',
                      width: '100%',
                    }}
                  >
                    {record['WD Serial No.'] && (
                      <WDSerialBadge text={record['WD Serial No.']} />
                    )}
                  </div>
                </div>
              </div>

              {/* FADED MATCHLESS LOGO */}
              <div
                className="wd-watermark"
                style={{
                  padding: '8px 26px 0',
                  display: 'flex',
                  marginTop: '-90px',
                  marginBottom: '-180px',
                  justifyContent: 'center',
                }}
              >
                <img
                  src={WDMatchlessLogo}
                  alt={record.Model}
                  style={{
                    width: '100%',
                    opacity: '0.07',
                    maxWidth: '340px',
                    height: 'auto',
                    filter: 'grayscale(0.9)',
                    objectFit: 'contain',
                  }}
                />
              </div>

              {/* BIKE IMAGE */}
              <div
                className="wd-bike-wrapper"
                style={{
                  padding: '8px 26px 0',
                  display: 'flex',
                  marginTop: '-95px',
                  marginBottom: '-60px',
                  justifyContent: 'center',
                }}
              >
                <img
                  src={bikeImage}
                  alt={record.Model}
                  style={{
                    width: '100%',
                    maxWidth: '640px',
                    height: 'auto',
                    filter: 'grayscale(0.099)',
                    objectFit: 'contain',
                  }}
                />
              </div>

              {/* MILITARY CLASS BAND */}
              <div className="wd-mil-band" style={{ margin: '18px 26px 0' }}>
                <div
                  style={{
                    height: '2px',
                    backgroundColor: '#4D4B1D',
                  }}
                />
                <div
                  className="wd-mil-text"
                  style={{
                    padding: '10px 0',
                    fontFamily: '"Trebuchet MS", system-ui, sans-serif',
                    fontSize: '25px',
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: '#2b2415',
                    fontWeight: 700,
                  }}
                >
                  Military Class: {militaryClass}
                </div>
                <div
                  style={{
                    height: '2px',
                    backgroundColor: '#4D4B1D',
                  }}
                />
              </div>

              {/* LOWER SECTION WITH FADED BACKGROUND BIKE */}
              <div
                className="wd-lower"
                style={{
                  position: 'relative',
                  overflow: 'hidden',
                  padding: '0px 0',
                }}
              >
                {/* Faded background bike silhouette */}
                <img
                  src={bikeImage}
                  alt=""
                  aria-hidden="true"
                  className="wd-faded-bike"
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    width: '70%',
                    opacity: 0.08,
                    transform: 'translate(-30%, -60%)',
                    filter: 'grayscale(1)',
                    pointerEvents: 'none',
                    userSelect: 'none',
                  }}
                />

                {/* SPEC BLOCK */}
                <div
                  className="wd-spec-grid"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto 1fr',
                    alignItems: 'center',
                    padding: '0px 26px 1px',
                    columnGap: '22px',
                  }}
                >
                  {/* LEFT COLUMN — TEXT */}
                  <div
                    className="wd-spec-left"
                    style={{
                      fontFamily: '"Trebuchet MS", system-ui, sans-serif',
                      fontSize: '18px',
                      lineHeight: 1.55,
                      padding: '10px 10px 10px',
                      color: '#242015',
                      textAlign: 'left',
                    }}
                  >
                    <strong>Frame No.:</strong> {record['Frame No.']} <br />
                    <strong>WD Serial No.:</strong> {record['WD Serial No.']} <br />
                    <strong>Engine No.:</strong> {record['Engine No.']} <br />
                    <strong>Contract Number:</strong> {record['Contract Number']} <br />
                    <strong>Price:</strong> {record['Price']}
                  </div>

                  {/* CENTER COLUMN — VERTICAL LINE */}
                  <div
                    className="wd-spec-divider"
                    style={{
                      width: '2px',
                      height: '100%',
                      backgroundColor: '#4D4B1D',
                      opacity: 0.65,
                      borderRadius: '2px',
                      justifySelf: 'center',
                    }}
                  />

                  {/* RIGHT COLUMN — TANK IMAGE */}
                  <div
                    className="wd-tank-wrapper"
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    {tankImage && (
                      <img
                        src={tankImage}
                        alt="Tank stamp"
                        className="wd-tank-img"
                        style={{
                          maxWidth: '250px',
                          maxHeight: '150px',
                          objectFit: 'contain',
                          filter: 'grayscale(1)',
                          opacity: 1,
                          marginTop: '10px',
                          transform: 'rotate(-8deg)',
                        }}
                      />
                    )}
                  </div>
                </div>

                {/* BOTTOM RULE + DELIVERY ROW */}
                <div
                  className="wd-bottom-rule"
                  style={{
                    margin: '0 26px 0',
                    height: '2px',
                    backgroundColor: '#4D4B1D',
                  }}
                />

                <div
                  className="wd-delivery"
                  style={{
                    padding: '10px 26px 20px',
                    fontFamily: '"Trebuchet MS", system-ui, sans-serif',
                    fontSize: '14px',
                    lineHeight: 1.6,
                    color: '#242015',
                  }}
                >
                  <span
                    style={{
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      fontWeight: 700,
                    }}
                  >
                    Delivery Destination:
                  </span>{' '}
                  {record['Delivery Destination']}
                </div>

                <div
                  className="wd-bottom-rule"
                  style={{
                    margin: '0 26px 0',
                    height: '2px',
                    backgroundColor: '#4D4B1D',
                  }}
                />

                {/* DATES ROW */}
                {record['Dates'] && (
                  <div
                    className="wd-dates-row"
                    style={{
                      padding: '5px 26px 18px',
                      fontFamily: '"Trebuchet MS", system-ui, sans-serif',
                      fontSize: '14px',
                      lineHeight: 1.6,
                      color: '#242015',
                    }}
                  >
                    <span
                      style={{
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        fontWeight: 700,
                      }}
                    >
                      Dates:
                    </span>{' '}
                    {record['Dates']}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Save Image button under the card */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          padding: '0.4rem 0.75rem 0.6rem',
        }}
      >
        <button
          type="button"
          onClick={handleDownloadImage}
          aria-label="Save poster image"
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            fontFamily: '"Trebuchet MS", system-ui, sans-serif',
            fontSize: '0.9rem',
            color: '#3b3625',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.35rem',
            opacity: 0.8,
          }}
          onMouseEnter={e => (e.currentTarget.style.opacity = 1)}
          onMouseLeave={e => (e.currentTarget.style.opacity = 0.8)}
        >
          <span>Save</span>
          <BsDownload size={18} color="#3b3625" />
        </button>
      </div>
    </Card>
  );
};

export default WDRecordCard;